/**
 * Generic CRUD over a collection of identified rings. Islands, rocks, and shallow-water zones
 * are all a `Ring` plus an `id` and a `color`; keeping their vertex, edge, and lookup operations
 * here means a new ring-shaped object type is a thin wrapper rather than another copy of
 * `islands.ts`.
 */

import { clampToBounds, clonePoint, distanceSquared } from './document.ts';
import { pointInPolygon } from './islands.ts';
import {
  clearEdgeBend,
  findBendHandleAt,
  findRingEdgeAt,
  moveBendHandle,
  moveRingVertex,
  removeRingVertex,
  setCornerRadius,
  setEdgeBend,
  tessellateRing,
} from './ring.ts';
import { cloneRgba, type Rgba } from '../color.ts';
import { MIN_RING_VERTICES, type MapBounds, type Ring, type Vec2 } from './types.ts';

export type IdentifiedRing = Ring & { id: string; color: Rgba };

export function findRingById<T extends IdentifiedRing>(
  items: readonly T[],
  id: string,
): T | undefined {
  return items.find((item) => item.id === id);
}

/** Topmost ring under `point`, matching the reverse paint order used everywhere else. */
export function findRingAtPoint<T extends IdentifiedRing>(
  items: readonly T[],
  point: Vec2,
): T | undefined {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];

    if (item && pointInPolygon(point, tessellateRing(item))) {
      return item;
    }
  }

  return undefined;
}

export function findRingVertex<T extends IdentifiedRing>(
  items: readonly T[],
  point: Vec2,
  radius: number,
): { id: string; index: number } | null {
  const radiusSq = radius * radius;
  let best: { id: string; index: number; dist: number } | null = null;

  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];

    if (!item) {
      continue;
    }

    for (let index = 0; index < item.vertices.length; index += 1) {
      const vertex = item.vertices[index];

      if (!vertex) {
        continue;
      }

      const dist = distanceSquared(point, vertex);

      if (dist <= radiusSq && (!best || dist < best.dist)) {
        best = { id: item.id, index, dist };
      }
    }
  }

  return best ? { id: best.id, index: best.index } : null;
}

export function moveRingVertexById<T extends IdentifiedRing>(
  items: readonly T[],
  bounds: MapBounds,
  id: string,
  index: number,
  point: Vec2,
): boolean {
  const item = findRingById(items, id);
  return item ? moveRingVertex(item, index, clampToBounds(point, bounds)) : false;
}

export function removeRingVertexById<T extends IdentifiedRing>(
  items: readonly T[],
  id: string,
  index: number,
): boolean {
  const item = findRingById(items, id);

  if (!item || item.vertices[index] === undefined) {
    return false;
  }

  if (item.vertices.length <= MIN_RING_VERTICES) {
    return false;
  }

  return removeRingVertex(item, index);
}

export function removeRingById<T extends IdentifiedRing>(items: T[], id: string): boolean {
  const index = items.findIndex((item) => item.id === id);

  if (index < 0) {
    return false;
  }

  items.splice(index, 1);
  return true;
}

export function setRingCornerRadiusById<T extends IdentifiedRing>(
  items: readonly T[],
  id: string,
  radius: number,
): boolean {
  const item = findRingById(items, id);

  if (!item) {
    return false;
  }

  setCornerRadius(item, radius);
  return true;
}

export function setRingColorById<T extends IdentifiedRing>(
  items: readonly T[],
  id: string,
  color: Rgba,
): boolean {
  const item = findRingById(items, id);

  if (!item) {
    return false;
  }

  item.color = cloneRgba(color);
  return true;
}

export function findRingEdge<T extends IdentifiedRing>(
  items: readonly T[],
  id: string,
  point: Vec2,
  radius: number,
): number | null {
  const item = findRingById(items, id);
  return item ? findRingEdgeAt(item, point, radius) : null;
}

export function findRingBend<T extends IdentifiedRing>(
  items: readonly T[],
  id: string,
  point: Vec2,
  radius: number,
): number | null {
  const item = findRingById(items, id);
  return item ? findBendHandleAt(item, point, radius) : null;
}

export function setRingEdgeBendById<T extends IdentifiedRing>(
  items: readonly T[],
  bounds: MapBounds,
  id: string,
  edgeIndex: number,
  point: Vec2,
): boolean {
  const item = findRingById(items, id);
  return item ? setEdgeBend(item, edgeIndex, clampToBounds(point, bounds)) : false;
}

export function moveRingBendById<T extends IdentifiedRing>(
  items: readonly T[],
  bounds: MapBounds,
  id: string,
  edgeIndex: number,
  point: Vec2,
): boolean {
  const item = findRingById(items, id);
  return item ? moveBendHandle(item, edgeIndex, clampToBounds(point, bounds)) : false;
}

export function clearRingEdgeBendById<T extends IdentifiedRing>(
  items: readonly T[],
  id: string,
  edgeIndex: number,
): boolean {
  const item = findRingById(items, id);
  return item ? clearEdgeBend(item, edgeIndex) : false;
}

export function makeRingVertices(bounds: MapBounds, vertices: readonly Vec2[]): Vec2[] {
  return vertices.map((vertex) => clampToBounds(clonePoint(vertex), bounds));
}
